/**
 * Which demonstration is on screen (SPEC_CAPTURE_UX_R2 §4).
 *
 * A module singleton, like the guidance player and the grid toggle: the sheet is mounted once by the Shell, the
 * capture screens ask for a demonstration, and the help sheet's "Show me" asks for the same one — with no provider
 * to thread through and no second copy of the open state.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { ScriptShot } from '@/api/capture';
import { track } from '@/flow/instrument';
import type { DemoType } from './demoTypes';
import { demosFor } from './demoTypes';
import { demoSeen, markDemoSeen } from './seenDemos';

export interface DemoOpen {
  readonly type: DemoType;
  /** True when the app opened it by itself on a first encounter — false when the customer tapped "Show me". */
  readonly auto: boolean;
}

let open: DemoOpen | null = null;
const listeners = new Set<() => void>();

function publish(): void {
  for (const l of listeners) l();
}

function snapshot(): DemoOpen | null {
  return open;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function openDemo(type: DemoType, auto = false): void {
  open = { type, auto };
  publish();
}

export function closeDemo(): void {
  if (!open) return;
  open = null;
  publish();
}

export function useDemoSheet(): { readonly open: DemoOpen | null; readonly show: (t: DemoType) => void; readonly close: () => void } {
  const current = useSyncExternalStore(subscribe, snapshot, () => null);
  const show = useCallback((type: DemoType) => openDemo(type, false), []);
  const close = useCallback(() => closeDemo(), []);
  return { open: current, show, close };
}

/**
 * The §4 invocation rule: the FIRST time this case meets a shot type, its demonstration plays without a tap.
 * Marked seen as it opens (per link), so a recapture of the same type — or a second shot of the same family —
 * never re-teaches. A shot whose prompt key we do not teach gets nothing, never an invented demonstration.
 */
export function useDemoAutoPlay(shot: Pick<ScriptShot, 'shotId' | 'promptKey' | 'orientation'> | null, enabled: boolean): void {
  const shotId = shot?.shotId ?? null;
  const promptKey = shot?.promptKey ?? null;
  const orientation = shot?.orientation ?? null;
  useEffect(() => {
    if (!enabled || !promptKey || !orientation) return;
    const unseen = demosFor({ promptKey, orientation }).find((t) => !demoSeen(t));
    if (!unseen) return;
    markDemoSeen(unseen);
    openDemo(unseen, true);
    track('showme.autoplay', { type: unseen, ...(promptKey ? { promptKey } : {}) });
  }, [enabled, shotId, promptKey, orientation]);
}

export function resetDemoStoreForTests(): void {
  open = null;
  publish();
}
